import { ClaudeAnalysisResponse } from '../types/aiAnalysis';
import { getFunctions, httpsCallable } from 'firebase/functions';

// All Claude calls go through the analyzeWithClaude Cloud Function;
// the model is configured server-side in functions/src/index.ts.

export class ClaudeApiService {
  private functions;

  constructor() {
    this.functions = getFunctions();
  }

  async analyzeImages(photos: string[], context?: string): Promise<ClaudeAnalysisResponse> {
    try {
      console.log('🤖 Analyzing images with Claude via Firebase Callable Function...');
      
      const analyzeWithClaude = httpsCallable(this.functions, 'analyzeWithClaude');
      const result = await analyzeWithClaude({
        photos,
        context
      });

      const data = result.data as any;
      console.log('✅ Claude analysis completed successfully');
      
      // Map the Cloud Function response to ClaudeAnalysisResponse format
      const analysis = data.analysis;
      return {
        brand: analysis.brand || undefined,
        model: analysis.model || undefined,
        year: analysis.year || undefined,
        estimatedValueMin: analysis.estimatedValue?.min || undefined,
        estimatedValueMax: analysis.estimatedValue?.max || undefined,
        valueConfidence: analysis.estimatedValue?.confidence || 0.5,
        category: analysis.category || 'Okänt föremål',
        condition: analysis.condition || undefined,
        description: analysis.description || undefined,
        materials: analysis.materials || undefined,
        dimensions: analysis.dimensions || undefined,
        additionalInfo: analysis.additionalInfo || undefined,
        overallConfidence: data.analysis.confidence || 0.5,
        reasoning: `Analyserad med Claude Sonnet 4 på ${data.analyzedAt}`
      };

    } catch (error) {
      console.error('Claude API analysis error:', error);
      throw error;
    }
  }

  /**
   * Convert image file to base64 string
   */
  private async convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        try {
          // Remove data URL prefix to get pure base64
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        } catch (error) {
          reject(new Error('Could not convert image to base64'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Could not read image file'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * System prompt for Claude - sets the context and rules
   */
  private getSystemPrompt(): string {
    return `Du är en professionell auktionsexpert som hjälper till att identifiera och värdera föremål för Stadsauktion Sundsvall. 

DITT UPPDRAG:
- Analysera bilderna noggrant och identifiera föremålet
- Ge realistiska värderingar baserat på svenska auktionspriser
- Var konservativ med värderingar - underskatta hellre än överskatta
- Använd alltid svenska termer och beskrivningar
- Fokusera på faktisk information, inte spekulationer

VIKTIGA RIKTLINJER:
- Om du är osäker på något, markera det tydligt med låg confidence
- Värderingar ska vara i SEK och spegla svenska auktionspriser
- Använd svenska termer för material, stilar och epoker
- Prioritera säkerhet över optimism i värderingar
- Ge konkret information som är användbar för auktionskataloger

SVARA ALLTID I VALID JSON-FORMAT med exakt dessa fält:
{
  "brand": "string eller null",
  "model": "string eller null", 
  "year": "string eller null",
  "estimatedValueMin": "number eller null",
  "estimatedValueMax": "number eller null",
  "valueConfidence": "number 0-1",
  "category": "string (obligatorisk)",
  "condition": "string eller null",
  "description": "string eller null",
  "materials": ["array av strings eller null"],
  "dimensions": "string eller null",
  "additionalInfo": ["array av strings eller null"],
  "overallConfidence": "number 0-1",
  "reasoning": "string med din motivering"
}`;
  }

  /**
   * Build the user prompt for image analysis
   */
  private buildAnalysisPrompt(imageCount: number, additionalContext?: string): string {
    let prompt = `Analysera dessa ${imageCount} bilder av ett auktionsföremål.

ANALYS INSTRUKTIONER:
1. Identifiera föremålet så specifikt som möjligt
2. Bestäm kategori (möbler, konst, glas, keramik, textil, etc.)
3. Identifiera märke/tillverkare om möjligt
4. Uppskatta ålder/period
5. Bedöm skick och kondition
6. Ge realistisk värdering för svenska auktioner

`;

    if (additionalContext) {
      prompt += `YTTERLIGARE KONTEXT FRÅN ANVÄNDAREN:
${additionalContext}

`;
    }

    prompt += `SVARA I VALID JSON-FORMAT enligt systemprompten. Var noggrann och konservativ med värderingar.`;

    return prompt;
  }

  /**
   * Parse Claude's response into structured data
   */
  private parseClaudeResponse(responseText: string): ClaudeAnalysisResponse {
    try {
      // Try to extract JSON from the response
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in Claude response');
      }

      const parsed = JSON.parse(jsonMatch[0]);
      
      // Validate required fields
      if (!parsed.category) {
        throw new Error('Missing required category field in Claude response');
      }

      if (typeof parsed.overallConfidence !== 'number' || 
          parsed.overallConfidence < 0 || 
          parsed.overallConfidence > 1) {
        parsed.overallConfidence = 0.5; // Default confidence
      }

      return {
        brand: parsed.brand || undefined,
        model: parsed.model || undefined,
        year: parsed.year || undefined,
        estimatedValueMin: parsed.estimatedValueMin || undefined,
        estimatedValueMax: parsed.estimatedValueMax || undefined,
        valueConfidence: this.normalizeConfidence(parsed.valueConfidence),
        category: parsed.category,
        condition: parsed.condition || undefined,
        description: parsed.description || undefined,
        materials: Array.isArray(parsed.materials) ? parsed.materials : undefined,
        dimensions: parsed.dimensions || undefined,
        additionalInfo: Array.isArray(parsed.additionalInfo) ? parsed.additionalInfo : undefined,
        overallConfidence: parsed.overallConfidence,
        reasoning: parsed.reasoning || undefined
      };
      
    } catch (error) {
      console.error('Failed to parse Claude response:', error);
      console.error('Original response:', responseText);
      
      // Return a fallback response
      return {
        category: 'Okänt föremål',
        overallConfidence: 0.1,
        reasoning: 'Kunde inte analysera bilden korrekt. Vänligen försök igen eller lägg till mer kontext.'
      };
    }
  }

  /**
   * Normalize confidence values to 0-1 range
   */
  private normalizeConfidence(value: any): number {
    if (typeof value !== 'number') return 0.5;
    return Math.max(0, Math.min(1, value));
  }

  /**
   * Check if API key is configured
   */
  isConfigured(): boolean {
    return !!this.functions;
  }

  /**
   * Update API key (for dynamic configuration)
   */
  updateApiKey(newApiKey: string): void {
    // This method is not applicable for Firebase callable functions
  }
} 